// ===========================================
// Business Card Review Card Component
// ===========================================
// Uses the exact same field layout as CompanyFormPage
// so the mobile experience matches the Add Company form.

import React from 'react';
import { Check, X, AlertTriangle, Loader2, ImageOff } from 'lucide-react';
import type { BusinessCardEntry } from '@/hooks/useBusinessCardImport';
import type { BusinessCardData } from '@/services/businessCardRecognition';

interface Props {
  card: BusinessCardEntry;
  companyTypes: string[];
  onUpdateField: (cardId: string, field: keyof BusinessCardData, value: string) => void;
  onApprove: (cardId: string) => void;
  onSkip: (cardId: string) => void;
  onSetAction: (cardId: string, action: 'new_company' | 'add_contact_to_existing', existingCompanyId?: string) => void;
}

export function BusinessCardReviewCard({
  card,
  companyTypes,
  onUpdateField,
  onApprove,
  onSkip,
  onSetAction,
}: Props) {
  const data = card.editedData || card.parsedData;
  const isEditable = card.status === 'parsed';
  const isApproved = card.status === 'approved';
  const isSkipped = card.status === 'skipped';

  // --- Loading state ---
  if (card.status === 'pending' || card.status === 'processing') {
    return (
      <div className="card">
        <div className="flex items-center gap-4">
          <img src={card.thumbnailUrl} alt="Business card" className="w-20 h-14 object-cover rounded border" />
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="animate-spin" size={20} />
            <span>{card.status === 'pending' ? 'Waiting...' : 'Scanning with AI...'}</span>
          </div>
        </div>
      </div>
    );
  }

  // --- Error state ---
  if (card.status === 'error') {
    return (
      <div className="card bg-red-50 border-red-200">
        <div className="flex items-center gap-4">
          <img src={card.thumbnailUrl} alt="Business card" className="w-20 h-14 object-cover rounded border" />
          <div className="flex-1">
            <div className="flex items-center gap-2 text-red-600 font-medium">
              <ImageOff size={18} />
              Failed to scan
            </div>
            <p className="text-sm text-red-500 mt-1">{card.error}</p>
          </div>
        </div>
      </div>
    );
  }

  // --- Parsed / Approved / Skipped ---
  return (
    <div className={`card ${isApproved ? 'border-green-400 bg-green-50' : isSkipped ? 'opacity-50' : ''}`}>

      {/* Photo + status row */}
      <div className="flex items-start gap-3 mb-4">
        <img src={card.thumbnailUrl} alt="Business card"
          className="w-28 h-[72px] object-cover rounded-lg border shadow-sm flex-shrink-0" />
        <div className="flex-1 min-w-0">
          {card.confidence && (
            <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${
              card.confidence === 'high' ? 'bg-green-100 text-green-700'
              : card.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700'
              : 'bg-red-100 text-red-700'
            }`}>
              {card.confidence} confidence
            </span>
          )}
          {card.warnings.length > 0 && (
            <p className="text-xs text-amber-600 mt-1">{card.warnings.join(' • ')}</p>
          )}
          {isApproved && <p className="text-sm font-semibold text-green-600 mt-1 flex items-center gap-1"><Check size={16} /> Approved</p>}
          {isSkipped && <p className="text-sm text-gray-400 mt-1 flex items-center gap-1"><X size={16} /> Skipped</p>}
        </div>
      </div>

      {/* Duplicate warning */}
      {card.duplicateMatch && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {card.duplicateMatch.matchType === 'exact' ? 'Exact match' : 'Similar company'}: "{card.duplicateMatch.companyName}"
              </p>
              <div className="mt-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name={`action-${card.id}`} checked={card.importAction === 'add_contact_to_existing'}
                    onChange={() => onSetAction(card.id, 'add_contact_to_existing', card.duplicateMatch!.companyId)}
                    disabled={!isEditable} className="w-4 h-4" />
                  <span className="text-sm">Add contact to existing company</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name={`action-${card.id}`} checked={card.importAction === 'new_company'}
                    onChange={() => onSetAction(card.id, 'new_company')}
                    disabled={!isEditable} className="w-4 h-4" />
                  <span className="text-sm">Create as new company</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === Form fields — identical layout to CompanyFormPage === */}
      <div className="space-y-6">

        {/* Company Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
          <input type="text" className="input" value={data?.companyName || ''} disabled={!isEditable}
            onChange={(e) => onUpdateField(card.id, 'companyName', e.target.value)} />
        </div>

        {/* Company Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Type</label>
          <select className="input" value={data?.companyType || ''} disabled={!isEditable}
            onChange={(e) => onUpdateField(card.id, 'companyType', e.target.value)}>
            <option value="">Select a type...</option>
            {companyTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/* Contact Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input type="text" className="input" value={data?.contactFirstName || ''} disabled={!isEditable}
              onChange={(e) => onUpdateField(card.id, 'contactFirstName', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input type="text" className="input" value={data?.contactLastName || ''} disabled={!isEditable}
              onChange={(e) => onUpdateField(card.id, 'contactLastName', e.target.value)} />
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title / Role</label>
          <input type="text" className="input" placeholder="e.g. Senior Adjuster, Owner"
            value={data?.contactTitle || ''} disabled={!isEditable}
            onChange={(e) => onUpdateField(card.id, 'contactTitle', e.target.value)} />
        </div>

        {/* Phone and Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input type="tel" className="input" placeholder="(555) 123-4567"
              value={data?.phone || ''} disabled={!isEditable}
              onChange={(e) => onUpdateField(card.id, 'phone', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" className="input" placeholder="contact@company.com"
              value={data?.email || ''} disabled={!isEditable}
              onChange={(e) => onUpdateField(card.id, 'email', e.target.value)} />
          </div>
        </div>

        {/* Street Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
          <input type="text" className="input" placeholder="123 Main St"
            value={data?.address || ''} disabled={!isEditable}
            onChange={(e) => onUpdateField(card.id, 'address', e.target.value)} />
        </div>

        {/* City, State, Zip — same grid as CompanyFormPage */}
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input type="text" className="input" placeholder="City"
              value={data?.city || ''} disabled={!isEditable}
              onChange={(e) => onUpdateField(card.id, 'city', e.target.value)} />
          </div>
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            <input type="text" className="input" placeholder="AZ" maxLength={2}
              value={data?.state || ''} disabled={!isEditable}
              onChange={(e) => onUpdateField(card.id, 'state', e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
            <input type="text" className="input" placeholder="85001"
              value={data?.zip || ''} disabled={!isEditable}
              onChange={(e) => onUpdateField(card.id, 'zip', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Action buttons — full width, match form button styling */}
      {isEditable && (
        <div className="flex gap-3 pt-6 border-t mt-6">
          <button type="button" onClick={() => onApprove(card.id)} className="btn btn-primary flex-1">
            <Check size={18} /> Approve
          </button>
          <button type="button" onClick={() => onSkip(card.id)} className="btn btn-secondary flex-1">
            <X size={18} /> Skip
          </button>
        </div>
      )}
    </div>
  );
}
